import { Component, EventEmitter, Input, OnDestroy, OnInit, Output } from '@angular/core';
import {
  CellClickedEvent,
  CellValueChangedEvent,
  CheckboxSelectionCallbackParams,
  ColumnApi,
  GridApi,
  GridOptions,
  GridReadyEvent,
  IRowNode,
  IServerSideGetRowsParams,
  RowDragMoveEvent,
  RowNode,
  RowSelectedEvent,
} from 'ag-grid-community';
import { CheckboxSelectionCallback, ColDef, ColGroupDef } from 'ag-grid-community/dist/lib/entities/colDef';
import { takeUntil } from 'rxjs/operators';
import { v4 as newGuid } from 'uuid';

import { AggFunc, ColourNames } from '../../../constants';
import { BaseComponent } from '../../base/base.component';
import { DeleteModalComponent } from '../../modals';
import { GridRowSelect, GridState, UserPreference } from '../../../models';
import {
  BaseImportService,
  FormatService,
  FormService,
  GridService,
  MenuService,
  ModalService,
  NotificationService,
  UserPreferenceService,
} from '../../../services';

@Component({
  selector: 'app-grid',
  templateUrl: './grid.component.html',
  styleUrl: './grid.component.scss',
})
export class GridComponent extends BaseComponent implements OnInit, OnDestroy {
  @Input() columnDefs?: (ColDef | ColGroupDef)[] = [];
  @Input() groupColumnHeader?: string;
  @Input() header: string;
  @Input() stateKey: string;
  @Input() layoutStateKey: string;
  @Input() filter = true;
  @Input() gridCssClass: string;
  @Input() pagination = true;
  @Input() pageSize = 100;
  @Input() resizable = true;
  @Input() rowData: any[] = [];
  @Input() multiSelectRows = true;
  @Input() multiSelectCells = false;
  @Input() showDelete = true;
  @Input() showDeleteMessage = true;
  @Input() showDiscard = true;
  @Input() showPrint = false;
  @Input() sortable = true;
  @Input() sizeToFit = false;
  @Input() suppressScrollOnNewData = false;
  @Input() importService: BaseImportService;
  @Input() chart = false;
  @Input() serverSide = false;
  @Output() delete = new EventEmitter<any>();
  @Output() print = new EventEmitter<any>();
  @Output() edit = new EventEmitter<any>();
  @Output() chartCellClicked = new EventEmitter<any>();
  @Output() rowSelected = new EventEmitter<GridRowSelect>();
  @Output() save = new EventEmitter<void>();
  @Output() imported = new EventEmitter<void>();
  @Output() savedState = new EventEmitter<void>();
  @Output() serverSideSearch = new EventEmitter<IServerSideGetRowsParams>();

  gridApi: GridApi;
  gridColumnApi: ColumnApi;
  state: GridState = {} as GridState;

  gridOptions: GridOptions;
  components: any;
  edited = false;
  updateFilter = true;

  private gridId: string;
  private modalLevel: number;
  private saveStateTimer: number;

  constructor(
    private modalService: ModalService,
    private gridService: GridService,
    private formService: FormService,
    private formatService: FormatService,
    private menuService: MenuService,
    private notificationService: NotificationService,
    private userPreferenceService: UserPreferenceService,
  ) {
    super();
  }

  ngOnInit() {
    const columnDefs = [...this.columnDefs] as ColDef[];

    if (this.showPrint) {
      columnDefs.push(
        this.gridService.button(
          (model: any) => this.print.emit(model),
          '',
          'print',
          'text-dark',
          'fa fa-print',
          undefined,
          { pinned: 'right' },
        ),
      );
    }

    if (this.showDelete && !this.menuService.isReadOnly) {
      columnDefs.push(
        this.gridService.button(
          (model: any) => {
            if (this.showDeleteMessage) {
              if (model.canDelete !== false) {
                this.modalService.openSmall(DeleteModalComponent).modal.result.then(
                  () => this.delete.emit(model),
                  () => {},
                );
              }
            } else {
              this.delete.emit(model);
            }
          },
          '',
          'delete',
          'text-danger',
          'fa fa-trash-alt',
          (model: any) => model.canDelete !== false,
          {
            showColumnFunc: (data: any[]) => data.some((x: any) => x.canDelete !== false),
            pinned: 'right',
          },
        ),
      );
    }

    if (this.serverSide) {
      this.columnDefs.forEach((x: ColDef) => (x.filter = undefined));
    }

    const rowDrag = columnDefs.some((x: ColDef) => x.rowDrag);

    this.gridOptions = {
      columnDefs: columnDefs,
      defaultColDef: {
        filter: this.filter && !this.serverSide,
        resizable: this.resizable,
        sortable: this.sortable && !this.serverSide,
        suppressMenu: !this.filter,
      },
      autoGroupColumnDef: {
        headerName: this.groupColumnHeader ?? columnDefs.find((x: ColDef) => x.rowGroup)?.headerName,
      },
      rowClassRules: {
        'bg-blue': (params) => params.data?.colour === ColourNames.blue,
        'bg-indigo': (params) => params.data?.colour === ColourNames.indigo,
        'bg-purple': (params) => params.data?.colour === ColourNames.purple,
        'bg-pink': (params) => params.data?.colour === ColourNames.pink,
        'bg-red': (params) => params.data?.colour === ColourNames.red,
        'bg-orange': (params) => params.data?.colour === ColourNames.orange,
        'bg-yellow': (params) => params.data?.colour === ColourNames.yellow,
        'bg-green': (params) => params.data?.colour === ColourNames.green,
        'bg-teal': (params) => params.data?.colour === ColourNames.teal,
        'bg-cyan': (params) => params.data?.colour === ColourNames.cyan,
        'bg-gray': (params) => params.data?.colour === ColourNames.gray,
        'bg-black': (params) => params.data?.colour === ColourNames.black,
      },
      pagination: this.pagination && !rowDrag,
      paginationPageSize: this.pageSize,
      rowDragManaged: rowDrag,
      suppressRowClickSelection: true,
      suppressRowTransform: true,
      suppressPropertyNamesCheck: true,
      suppressAggFuncInHeader: true,
      suppressMultiRangeSelection: true,
      stopEditingWhenCellsLoseFocus: true,
      suppressColumnVirtualisation: this.sizeToFit,
      enableRangeSelection: this.multiSelectCells,
    };

    if (this.serverSide) {
      this.gridOptions.rowModelType = 'serverSide';
      this.gridOptions.cacheBlockSize = this.pageSize;
      this.gridOptions.serverSideDatasource = {
        getRows: (params: IServerSideGetRowsParams) => this.serverSideSearch.emit(params),
      };
    }

    this.components = this.gridService.components;

    const checkboxRowSelect = columnDefs.find((x) => x.checkboxSelection);
    if (checkboxRowSelect) {
      this.gridOptions.rowSelection = this.multiSelectRows ? 'multiple' : 'single';
      this.gridOptions.rowMultiSelectWithClick = true;
      this.gridOptions.isRowSelectable = (rowNode: IRowNode) =>
        (checkboxRowSelect.checkboxSelection as CheckboxSelectionCallback).call(this, {
          data: rowNode.data,
        } as CheckboxSelectionCallbackParams);
    }

    this.modalLevel = this.modalService.modalLevel;
    this.formService.save.pipe(takeUntil(this.destroy$)).subscribe((modalLevel: number) => {
      if (modalLevel === this.modalLevel && this.edited) {
        this.save.emit();
      }
    });

    this.gridId = newGuid();
    this.formService.addGrid(this.modalLevel, this.gridId, this);
  }

  ngOnDestroy() {
    super.ngOnDestroy();

    this.clearSaveStateTimer();
    this.formService.removeGrid(this.modalLevel, this.gridId);
    this.gridApi = undefined;
    this.gridColumnApi = undefined;
  }

  protected get showReset() {
    return this.filter || this.sortable || this.resizable;
  }

  public get selectedIds(): string[] {
    return this.selectedRows.map((x) => x.id);
  }

  public get selectedRows(): any[] {
    const selectedRows = [];
    this.forEachRow((node) => {
      if (node.isSelected()) {
        selectedRows.push(node.data);
      }
    });
    return selectedRows;
  }

  public updateRow(model: any): boolean {
    this.forEachRow((rowNode: RowNode) => {
      if (rowNode.data.id === model.id) {
        rowNode.setData(model);
        return true;
      }
    });

    return false;
  }

  public get filteredRowData(): any[] {
    const filteredRowData = [];
    this.forEachRow((rowNode: RowNode) => filteredRowData.push(rowNode.data));
    return filteredRowData;
  }

  public get sortedAndFilteredRowData(): any[] {
    const filteredRowData = [];
    this.forEachRowAfterFilterAndSort((rowNode: RowNode) => filteredRowData.push(rowNode.data));
    return filteredRowData;
  }

  protected get initialRowData(): any[] {
    return this.serverSide ? undefined : this.rowData;
  }

  protected get isEdited() {
    return this.edited;
  }

  private get userPreferenceKey() {
    return this.layoutStateKey ?? (this.stateKey ? `Grid.${this.stateKey}` : '');
  }

  public resetEdited() {
    this.edited = false;
  }

  public export(fileName: string) {
    this.gridApi?.exportDataAsCsv({ fileName });
  }

  public loadState() {
    if (!this.stateKey) {
      return;
    }

    this.updateFilter = false;
    this.userPreferenceService
      .get(this.userPreferenceKey)
      .pipe(takeUntil(this.destroy$))
      .subscribe((userPreference: UserPreference) => {
        if (userPreference?.value) {
          this.state = JSON.parse(userPreference.value) as GridState;
          this.updateFilter = true;
          this.applyState();
        }
      });
  }

  public applyState() {
    if (this.state) {
      this.gridColumnApi?.applyColumnState({ state: this.state.columns, applyOrder: true });
      this.gridApi?.setFilterModel(this.state.filter);
    }
  }

  public setFilter(filter: any) {
    const existingFilter = this.gridApi?.getFilterModel();
    this.gridApi?.setFilterModel({ ...existingFilter, ...filter });
  }

  public calculateAggregates() {
    if (this.columnDefs.some((x: ColDef) => x.aggFunc)) {
      const data = this.calculatePinnedBottomData();
      if (Object.keys(data).some((key) => key !== 'isAggregate' && data[key])) {
        this.gridApi?.setPinnedBottomRowData([data]);
      }
    }
  }

  public import(file: any) {
    this.importService
      .import(file)
      .pipe(takeUntil(this.destroy$))
      .subscribe(() => {
        this.imported.emit();
        this.notificationService.success($localize`Import completed successfully`);
      });
  }

  public exportFile() {
    this.importService.export().pipe(takeUntil(this.destroy$)).subscribe();
  }

  public exportTemplateAndDataFile() {
    this.importService.exportTemplateAndData().pipe(takeUntil(this.destroy$)).subscribe();
  }

  public forEachRow(callback: (rowNode: RowNode) => void) {
    if (this.serverSide) {
      this.gridApi?.forEachNode((rowNode: RowNode) => {
        if (rowNode.data) {
          callback(rowNode);
        }
      });
    } else {
      this.gridApi?.forEachNodeAfterFilter((rowNode: RowNode) => callback(rowNode));
    }
  }

  private forEachRowAfterFilterAndSort(callback: (rowNode: RowNode) => void) {
    if (this.serverSide) {
      this.gridApi?.forEachNode((rowNode: RowNode) => {
        if (rowNode.data) {
          callback(rowNode);
        }
      });
    } else {
      this.gridApi?.forEachNodeAfterFilterAndSort((rowNode: RowNode) => callback(rowNode));
    }
  }

  protected clearSaveStateTimer() {
    if (this.saveStateTimer) {
      clearTimeout(this.saveStateTimer);
      this.saveStateTimer = undefined;
    }
  }

  protected onCellClicked(event: CellClickedEvent) {
    if (event.colDef.onCellClicked) {
      return;
    }

    if (this.chart) {
      this.chartCellClicked.emit({
        rowIndex: event.rowIndex,
        colIndex: this.getColumnIndex(event),
      });
    } else {
      if (event.data.canEdit !== false && !event.colDef.editable) {
        this.edit.emit(event.data);
      }
    }
  }

  protected saveState(updateFilterState = false) {
    if (!this.stateKey || !this.gridColumnApi || !this.gridApi) {
      return;
    }

    this.clearSaveStateTimer();
    this.saveStateTimer = setTimeout(() => {
      const previousState = JSON.stringify(this.state);
      this.state.columns = this.gridColumnApi?.getColumnState();
      if (updateFilterState) {
        this.state.filter = this.gridApi?.getFilterModel();
      }
      const newState = JSON.stringify(this.state);
      if (newState !== previousState) {
        this.userPreferenceService.set(this.userPreferenceKey, newState).subscribe();
      }
      this.calculateAggregates();
      this.savedState.emit();
    }, 100);
  }

  protected onRowDragMove(event: RowDragMoveEvent) {
    this.setEdited();
  }

  protected onRowSelected(event: RowSelectedEvent) {
    if (event.node.isSelected() === event.data.selected) {
      return;
    }
    this.setEdited();
    event.data.selected = event.node.isSelected();
    event.api.refreshCells({ force: true });

    setTimeout(
      () =>
        this.rowSelected.emit({
          model: event.data,
          selected: event.node.isSelected(),
          rowIndex: event.rowIndex,
          node: event.node,
        }),
      event.rowIndex * 10,
    );
  }

  protected gridReady(params: GridReadyEvent) {
    this.gridApi = params.api;
    this.gridColumnApi = params.columnApi;
    setTimeout(() => this.loadState(), 100);
  }

  protected cellValueChanged(event: CellValueChangedEvent) {
    this.setEdited();
    this.calculateAggregates();
  }

  protected rowDataUpdated() {
    setTimeout(() => {
      this.hideColumns();
      this.calculateAggregates();
      this.updateCheckboxSelection();
      this.applyState();
    }, 0);
  }

  protected filterChanged() {
    this.hideColumns();
    this.saveState(this.updateFilter);
  }

  protected resetColumns() {
    this.gridColumnApi?.resetColumnState();
  }

  protected resetFilters() {
    this.gridApi?.setFilterModel(undefined);
  }

  private getColumnIndex(event: CellClickedEvent): number {
    const field = event.colDef.field;
    return event.columnApi.getColumns()?.findIndex((col) => col.getColDef().field === field);
  }

  private setEdited() {
    if (this.save.observed && this.showDiscard) {
      this.edited = true;
    }
  }

  private hideColumns() {
    this.columnDefs.forEach((col: ColDef | any) => {
      if (col.showColumnFunc) {
        this.gridColumnApi?.setColumnVisible(col.field, col.showColumnFunc(this.filteredRowData));
      }
    });
  }

  private calculatePinnedBottomData() {
    const data = {
      isAggregate: true,
    };

    this.columnDefs.forEach((col: ColDef | any) => {
      if (col.showAggFunc && !col.showAggFunc(this.filteredRowData)) {
        return;
      }

      const field = col.field;
      if (col.customAggFunc) {
        data[field] = col.customAggFunc(this.filteredRowData);
        return;
      }

      switch (col.aggFunc) {
        case AggFunc.avg:
          data[field] = this.formatService.number(this.average(col, field, false));
          break;
        case AggFunc.count:
          data[field] = this.formatService.number(this.count(), 0);
          break;
        case AggFunc.first:
          break;
        case AggFunc.sum:
          data[field] = this.formatService.number(this.sum(col, field, false));
          break;
        case AggFunc.sumInteger:
          data[field] = this.formatService.number(this.sum(col, field, false), 0);
          break;
      }

      switch (col.groupAggFunc) {
        case AggFunc.avg:
          data[field] = this.formatService.number(this.average(col, field, true), 0);
          break;
        case AggFunc.count:
          data[field] = this.formatService.number(this.count(), 0);
          break;
        case AggFunc.first:
          break;
        case AggFunc.sum:
          data[field] = this.formatService.number(this.sum(col, field, true));
          break;
        case AggFunc.sumInteger:
          data[field] = this.formatService.number(this.sum(col, field, true), 0);
          break;
      }
    });

    return data;
  }

  private count(): number {
    let count = 0;
    this.forEachRow(() => count++);
    return count;
  }

  private sum(col: any, field: any, group: boolean): number {
    const calculation = col.calculation;
    let sum = 0;

    this.forEachRow((rowNode: RowNode) => {
      const data = group ? rowNode.aggData : rowNode.data;
      if (data) {
        const value = calculation ? calculation(data) : data[field];
        if (value) {
          sum += this.formatService.parseNumberString(value);
        }
      }
    });
    return sum;
  }

  private average(col: any, field: any, group: boolean): number {
    const sum = this.sum(col, field, group);
    const count = this.count();
    return count ? sum / count : 0;
  }

  public updateCheckboxSelection() {
    setTimeout(() => this.forEachRow((rowNode: RowNode) => rowNode.setSelected(rowNode.data?.selected ?? false)), 0);
  }
}
